import b from 'b_';
import React, { useLayoutEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { SimpleCallback } from '../../typings';
import { isTouchDevice } from '../../utils/device';
import Image from '../image/image';
import Text from '../text/text';

import './problem-bar-message.scss';
import { A11yHidden } from '../A11yHidden';

interface Props {
    text: string;
    imageSrc?: string;
    backgroundColor: string;
    controlColor?: string;
    closed: boolean;
    theme?: PROBLEM_BAR_MESSAGE_THEMES;
    onOpen?: SimpleCallback;
    onClose?: SimpleCallback;
    animationDuration?: number;
}

enum PROBLEM_BAR_MESSAGE_THEMES {
    DEFAULT = 'default',
    LITE = 'lite',
}

export const ProblemBarMessage = (props: Props) => {
    const {
        text,
        imageSrc,
        backgroundColor,
        controlColor,
        onOpen,
        onClose,
        closed,
        theme = PROBLEM_BAR_MESSAGE_THEMES.DEFAULT,
        animationDuration = 200,
    } = props;
    const ref = useRef<HTMLDivElement>(null);
    const messageRef = useRef<HTMLDivElement>(null);

    const intl = useIntl();

    useLayoutEffect(() => {
        const wrapperHeight = messageRef.current?.offsetHeight;
        if (wrapperHeight && ref.current?.style) {
            ref.current.style.maxHeight = `${wrapperHeight + 36}px`;
        }
    }, [text]);

    const shouldShowCurtainControl = isTouchDevice && theme === PROBLEM_BAR_MESSAGE_THEMES.DEFAULT;
    const shouldShowImage = imageSrc && theme === PROBLEM_BAR_MESSAGE_THEMES.DEFAULT;

    const handleClick = () => {
        // без картинок не нужна кликабельность, ибо вердикт всегда открыт
        if (!shouldShowImage) {
            return;
        }

        if (closed && onOpen) {
            onOpen();
        }

        if (!closed && onClose) {
            onClose();
        }
    };

    const transitionDuration = `${animationDuration}ms`;

    return (
        <div
            className={b('problem-bar-message', 'wrapper', { closed, theme })}
            ref={ref}
            style={{ backgroundColor, transitionDuration }}
        >
            {shouldShowCurtainControl && <div className="problem-bar-message__curtain" onClick={handleClick} />}
            <div className="problem-bar-message__container">
                <div
                    className={b('problem-bar-message', { 'only-text': !shouldShowImage })}
                    onClick={handleClick}
                    ref={messageRef}
                    style={{ transitionDuration }}
                >
                    <div className={b('problem-bar-message', 'content', { 'only-text': !shouldShowImage })}>
                        <div className="problem-bar-message__text">
                            <A11yHidden>{intl.formatMessage({ id: 'a11y.verdict' })}:</A11yHidden>
                            <Text>{text}</Text>
                        </div>
                    </div>
                </div>
                {shouldShowImage && (
                    <div className="problem-bar-message__img-wrapper" style={{ transitionDuration }}>
                        <svg fill="none" height="118" viewBox="0 0 100 118" width="100">
                            <path
                                d="M0 118H100V0C62.6154 0 43.923 0 30 8.03847C20.8788 13.3046 13.3046 20.8788 8.03848 30C0 43.923 0 62.6154 0 100V118Z"
                                fill={controlColor}
                            />
                        </svg>
                        <Image alt="" className="problem-bar-message__img" src={imageSrc} />
                    </div>
                )}
            </div>
        </div>
    );
};

ProblemBarMessage.THEME = PROBLEM_BAR_MESSAGE_THEMES;
